import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { addCartItems, getAllCartItems, sendNotifyRequest, } from "../../../../redux/slices/addToCart";
import { fetchUserWishlists, createWishlist, addToWishlist, updateWishlistQuantity } from "../../../../redux/slices/wishlist";

const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;

const checkCombinationMatch = (
  combination,
  selectedValuesMap,
  variantNameMapping
) => {
  if (!combination?.combination_attributes || !selectedValuesMap) return false;
  if (
    Object.keys(selectedValuesMap).length !==
    combination.combination_attributes.length
  )
    return false;

  return combination.combination_attributes.every((comboAttr) => {
    const attributeName = variantNameMapping[comboAttr.attribute_id]?.variant;
    if (!attributeName) return false;
    const selectedValueForAttr = selectedValuesMap[attributeName]?.value;
    return selectedValueForAttr === comboAttr.value;
  });
};

const transformSelectedVariants = (selectedVals) => {
  return Object.entries(selectedVals).map(([attribute, { value }]) => ({
    attribute,
    value,
  }));
};

const QuickViewModal = ({ product, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [matchedCombination, setMatchedCombination] = useState(null);
  const [displayPrice, setDisplayPrice] = useState(0);
  const [displayDiscountPrice, setDisplayDiscountPrice] = useState(0);
  const [isProductOutOfStock, setIsProductOutOfStock] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  // New state for low stock message
  const [lowStockMessage, setLowStockMessage] = useState("");


  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [userWishlists, setUserWishlists] = useState({});
  const [newListName, setNewListName] = useState("");
  const [wishlistStatus, setWishlistStatus] = useState(false);

  const userId = JSON.parse(localStorage.getItem("user"))?.id || "guest";
  const cartQuantitiesKey = `cart_quantities_${userId}`;
  const cartItemIdsKey = `cart_item_ids_${userId}`;

  const allCartData = useSelector((state) => state.addToCart.allCartItems || {});
  const allCartItems = Array.isArray(allCartData.items) ? allCartData.items : [];

  const {
    id: productId,
    name,
    description,
    variant_name = {},
    variant_value = [],
    variant_combinations = [],
    is_variant,
    price: basePrice,
    discount_price: baseDiscountPrice,
    stock_quantity: baseStockQuantity,
    stock_status: baseStockStatus,
  } = product;

  useEffect(() => {
    const loadCartData = async () => {
      if (userId === "guest") return;

      const res = await dispatch(getAllCartItems(userId));
      if (!res?.items?.length) return;

      const freshCartItemIds = {};
      const freshCartQuantities = {};

      res.items.forEach((item) => {
        freshCartItemIds[item.item_id] = item.id;
        freshCartQuantities[item.item_id] = item.quantity;
      });
      localStorage.setItem(cartItemIdsKey, JSON.stringify(freshCartItemIds));
      localStorage.setItem(
        cartQuantitiesKey,
        JSON.stringify(freshCartQuantities)
      );
    };

    loadCartData();
  }, [dispatch, userId, cartQuantitiesKey, cartItemIdsKey]);

  useEffect(() => {
    const loadWishlists = async () => {
      const user_id = JSON.parse(localStorage.getItem("user"))?.id;
      if (!user_id) return;

      const response = await dispatch(fetchUserWishlists(user_id, toast));
      if (response?.success && response.data) {
        const wishlistsObj = {};
        let foundInWishlist = false;
        for (const list of response.data) {
          wishlistsObj[list.name || "Default"] = {
            id: list.id,
            items: list.items || [],
          };
          if (list.items.some((item) => item.product_id === productId)) {
            foundInWishlist = true;
          }
        }
        setUserWishlists(wishlistsObj);
        setWishlistStatus(foundInWishlist);
      }
    };

    loadWishlists();
  }, [dispatch, productId]);

  useEffect(() => {
    if (!productId) return;

    let currentStock = 0;

    if (is_variant === "true" && variant_combinations.length > 0) {
      const defaultCombination =
        variant_combinations.find(
          (combo) => combo.stock_quantity > 0 && combo.stock_status === "in_stock"
        ) || variant_combinations[0];

      if (defaultCombination) {
        setMatchedCombination(defaultCombination);
        const initialSelected = {};
        defaultCombination.combination_attributes.forEach((attr) => {
          const attributeName = variant_name[attr.attribute_id]?.variant;
          const variantType = variant_value.find(
            (vt) => vt.id.toString() === attr.attribute_id.toString()
          );
          const fullValueObject = variantType?.multiple_values.find(
            (mv) => mv.value === attr.value
          );

          if (attributeName && fullValueObject) {
            initialSelected[attributeName] = {
              id: fullValueObject.id,
              value: fullValueObject.value,
            };
          }
        });
        setSelectedAttributeValues(initialSelected);
        setQuantity(1);
        currentStock = defaultCombination.stock_quantity;
      } else {
        setMatchedCombination(null);
        setIsProductOutOfStock(true);
        setDisplayPrice(0);
        setDisplayDiscountPrice(0);
        setQuantity(1);
        currentStock = 0;
      }
    } else if (is_variant === "false") {
      setMatchedCombination(null);
      setDisplayPrice(parseFloat(basePrice || 0));
      setDisplayDiscountPrice(parseFloat(baseDiscountPrice || basePrice || 0));
      setIsProductOutOfStock(baseStockQuantity === 0 || baseStockStatus === "out_of_stock");
      setQuantity(1);
      currentStock = baseStockQuantity;
    }

    if(currentStock > 0 && currentStock <= 5) {
      setLowStockMessage(`Hurry up! Only ${currentStock} quantity left`);
    } else {
      setLowStockMessage("");
    }

  }, [productId, is_variant, variant_combinations, variant_name, variant_value, basePrice, baseDiscountPrice, baseStockQuantity, baseStockStatus]);

  useEffect(() => {
    if (
      is_variant === "true" &&
      Object.keys(selectedAttributeValues).length > 0 &&
      variant_combinations.length > 0
    ) {
      const foundCombination = variant_combinations.find((combo) =>
        checkCombinationMatch(combo, selectedAttributeValues, variant_name)
      );
      setMatchedCombination(foundCombination);

      const allRequiredAttributesSelected = Object.values(variant_name).every(
        (vn) => selectedAttributeValues[vn.variant] !== undefined
      );
    } else if (is_variant === "false") {
      setMatchedCombination(null);
    }
  }, [selectedAttributeValues, variant_combinations, is_variant, variant_name]);

  useEffect(() => {
    let currentStock = 0;
    if (matchedCombination) {
      setDisplayPrice(parseFloat(matchedCombination.price || 0));
      setDisplayDiscountPrice(
        parseFloat(matchedCombination.discount_price || matchedCombination.price || 0)
      );
      setIsProductOutOfStock(
        matchedCombination.stock_quantity === 0 ||
          matchedCombination.stock_status === "out_of_stock"
      );
      currentStock = matchedCombination.stock_quantity;
    } else if (is_variant === "false") {
      setDisplayPrice(parseFloat(basePrice || 0));
      setDisplayDiscountPrice(parseFloat(baseDiscountPrice || basePrice || 0));
      setIsProductOutOfStock(baseStockQuantity === 0 || baseStockStatus === "out_of_stock");
      currentStock = baseStockQuantity;
    } else {
      setDisplayPrice(0);
      setDisplayDiscountPrice(0);
      setIsProductOutOfStock(true);
      currentStock = 0;
    }

    // Update low stock message whenever matchedCombination or baseStockQuantity changes
    if (currentStock > 0 && currentStock < 5) {
      setLowStockMessage(`Hurry up! Only ${currentStock} left in stock!`);
    } else {
      setLowStockMessage("");
    }
  }, [
    matchedCombination,
    is_variant,
    basePrice,
    baseDiscountPrice,
    baseStockQuantity,
    baseStockStatus,
  ]);

  useEffect(() => {
    if (!productId) return;

    const isAlreadyInCart = allCartItems.some((item) => {
      if (is_variant === "true" && matchedCombination) {
        return item.item_id === productId && item.variant_id === matchedCombination.id;
      }
      return item.item_id === productId && !item.variant_id;
    });

    setIsInCart(isAlreadyInCart);
  }, [allCartItems, productId, is_variant, matchedCombination]);

  const handleIncrement = () => {
    let maxStock = 0;
    if (is_variant === "true" && matchedCombination) {
      maxStock = matchedCombination.stock_quantity;
    } else if (is_variant === "false") {
      maxStock = baseStockQuantity;
    }

    if (maxStock > 0 && quantity + 1 > maxStock) {
      toast.error(`Only ${maxStock} items available in stock.`);
      return;
    }
    setQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleSelectValue = (variantAttributeName, selectedValueObject) => {
    setSelectedAttributeValues((prev) => ({
      ...prev,
      [variantAttributeName]: selectedValueObject,
    }));
    setQuantity(1);
  };

  const handleAddToCart = async () => {
    if (isProductOutOfStock) {
      toast.error("This product is out of stock.");
      return;
    }

    const cart_id = localStorage.getItem("cart_id")
      ? Number(localStorage.getItem("cart_id"))
      : null;
    const customer_id = JSON.parse(localStorage.getItem("user"))?.id || null;

    const baseCartItem = {
      item_id: productId,
      quantity,
      price: parseFloat(matchedCombination?.price || basePrice),
      discount_price: parseFloat(
        matchedCombination?.discount_price ||
          matchedCombination?.price ||
          baseDiscountPrice ||
          basePrice
      ),
      ...(cart_id && { cart_id }),
      ...(customer_id && { customer_id }),
    };

    const cartItemPayload =
      is_variant === "true" && matchedCombination
        ? {
            ...baseCartItem,
            variant_id: matchedCombination.id,
            selectedVariants: transformSelectedVariants(selectedAttributeValues),
          }
        : { ...baseCartItem, variant_id: null, selectedVariants: [] };

    const res = await dispatch(addCartItems(cartItemPayload, toast, navigate));

    if (res?.success && customer_id) {
      dispatch(getAllCartItems(customer_id));
    }
  };

  const handleNotifyMe = () => {
    const userEmail = JSON.parse(localStorage.getItem("user"))?.email;
    if (!userEmail) {
      toast.error("User Email is not found. Please log in.");
      navigate("/login");
      return;
    }

    if (is_variant === "true" && (!matchedCombination )) {
      toast.error("Please select a specific variant to be notified about.");
      return;
    }

    const payload = {
      product_id: productId,
      variant_id: matchedCombination?.id || null,
      email: userEmail,
    };
    dispatch(sendNotifyRequest(payload, toast));
  };

  const handleAddToList = async (listName = "Default", product_id) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const user_id = user?.id;

    const isCurrentProductVariant = is_variant === "true";
    let selectedWishlistItemDetails;

    if (isCurrentProductVariant && matchedCombination) {
      selectedWishlistItemDetails = {
        id: matchedCombination.id,
        value: Object.values(selectedAttributeValues)
          .map((val) => val.value)
          .join(" / "),
        price: matchedCombination.price ?? 0,
        discount_price: matchedCombination.discount_price ?? matchedCombination.price ?? 0,
        stock_quantity: matchedCombination.stock_quantity ?? 0,
        stock_status: matchedCombination.stock_status,
      };
    } else {
      selectedWishlistItemDetails = {
        id: null,
        value: "Default",
        price: basePrice ?? 0,
        discount_price: baseDiscountPrice ?? basePrice ?? 0,
        stock_quantity: baseStockQuantity ?? 0,
        stock_status: baseStockStatus,
      };
    }

    if (isCurrentProductVariant && (!matchedCombination )) {
      toast.error("Please select a valid variant to add to wishlist.");
      return;
    }

    let updatedWishlists = { ...userWishlists };

    if (!updatedWishlists[listName]) {
      const res = await dispatch(createWishlist(user_id, listName));
      const newId = res?.data?.id;

      if (!newId) {
        console.error("Wishlist creation failed:", res);
        return toast.error("Failed to create wishlist");
      }

      updatedWishlists[listName] = { id: newId, items: [] };
      setUserWishlists(updatedWishlists);
    }

    const wishlistId = updatedWishlists[listName].id;
    const items = updatedWishlists[listName]?.items || [];

    let selectedVariantsPayload = {};
    if (isCurrentProductVariant && matchedCombination) {
      Object.entries(selectedAttributeValues).forEach(([name, { id, value }]) => {
      selectedVariantsPayload[name.toLowerCase()] = {
        value,
        stock_quantity: matchedCombination.stock_quantity ?? 0,
        stock_status: matchedCombination.stock_status,
      };
      });
    }

    const existingItem = items.find((item) => {
      return (
        String(item.product_id) === String(product_id) &&
        String(item.variant_id || '') === String(matchedCombination?.id || '') &&
        String(item.wishlist_id) === String(wishlistId)
      );
    });

    if(existingItem) {
      const newQty = (existingItem.quantity || 1) + 1;
      const updated = await dispatch(updateWishlistQuantity(existingItem.id, newQty, toast));

      if (updated?.success) {
        toast.success("Quantity updated in wishlist");
        const updatedItems = items.map((item) =>
          item.id === existingItem.id ? { ...item, quantity: newQty } : item
        );
        setUserWishlists((prev) => ({
          ...prev,
          [listName]: { ...prev[listName], items: updatedItems },
        }));
      } else {
        toast.error("Failed to update quantity");
      }
      setShowWishlistModal(false);
      return;
    }
    
    const payload = {
      product_id: product_id,
      variant_id: matchedCombination?.id || null,
      selected_variants: selectedVariantsPayload,
      price: selectedWishlistItemDetails.price,
      discount_price: selectedWishlistItemDetails.discount_price
    };

    const added = await dispatch(addToWishlist(wishlistId, payload, toast));

    if (!added?.success) {
      console.error("Add to wishlist failed:", added);
      return toast.error(added?.message || "Failed to add to wishlist");
    }

    const refreshed = await dispatch(fetchUserWishlists(user_id, toast));
    const wishlistsArray = refreshed?.wishlists || refreshed?.data || [];

    const wishlistsByName = {};
    wishlistsArray.forEach((list) => {
      wishlistsByName[list.name] = {
        id: list.id,
        items: list.items || [],
      };
    });

    setUserWishlists(wishlistsByName);
    setWishlistStatus(true);
    setShowWishlistModal(false);
  };

  const handleCreateNewList = (product_id) => {
    if (!newListName.trim()) return toast.error("List name required");
    if (userWishlists[newListName]) {
      toast.info("List already exists");
      return;
    }
    handleAddToList(newListName, product_id);
    setNewListName("");
  };

  const displayStockStatusText = isProductOutOfStock ? "Out of Stock" : "In Stock";

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-50 p-4 font-inter ${
        showWishlistModal ? "bg-black bg-opacity-50 backdrop-blur-sm" : "bg-black bg-opacity-50"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl p-6 w-full max-w-3xl relative shadow-lg overflow-y-auto max-h-[90vh]"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold rounded-full w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Close"
        >
          &times;
        </button>

        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/2 flex justify-center items-center p-4 bg-gray-50 rounded-lg">
            <img
              src={
                product?.additional_images?.[0]
                  ? BASE_IMAGE_URL + product.additional_images[0]
                  : "https://placehold.co/300x300/e5e7eb/555555?text=Product+Image"
              }
              alt={name || "Product Image"}
              className="max-h-80 object-contain rounded-lg shadow-sm"
            />
          </div>

          <div className="md:w-1/2 flex flex-col p-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold self-start mb-2 ${
                isProductOutOfStock ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
              }`}
            >
              {displayStockStatusText}
            </span>
            {lowStockMessage && (
              <p className="text-red-600 font-semibold mt-2">
                {lowStockMessage}
              </p>
            )}

            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">{name}</h2>
            <p className="text-gray-600 text-sm mb-4">{description}</p>

            <div className="mb-4">
              {displayDiscountPrice > 0 && displayDiscountPrice < displayPrice ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-gray-500 line-through text-lg">
                    ₹{displayPrice.toFixed(2)}
                  </span>
                  <span className="text-3xl font-bold text-[#00A762]">
                    ₹{displayDiscountPrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-green-600 font-bold">
                    ({Math.round(((displayPrice - displayDiscountPrice) / displayPrice) * 100)}% off)
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  ₹{displayPrice.toFixed(2)}
                </span>
              )}
            </div>

            {is_variant === "true" && variant_value?.length > 0 && (
              <div className="space-y-6">
                {Object.values(variant_name).map((variantTypeInfo) => {
                  const matchedValues = variant_value.find(
                    (v) => v.id === variantTypeInfo.id
                  );
                  if (!matchedValues?.multiple_values.length) return null;
            
                  return (
                    <div key={variantTypeInfo.id}>
                      <h3 className="text-lg font-semibold mb-2 capitalize text-gray-800">
                        {variantTypeInfo.variant}
                      </h3>
                      <div className="flex gap-2 flex-wrap">
                        {matchedValues.multiple_values.map((value) => {
                          const isOut = !variant_combinations.some(
                            (combo) =>
                              combo.combination_attributes.some(
                                (attr) =>
                                  attr.attribute_id.toString() ===
                                    variantTypeInfo.id.toString() &&
                                  attr.value === value.value
                              ) &&
                              (combo.stock_quantity > 0 && combo.stock_status === "in_stock")
                          );
            
                          const isSelected =
                            selectedAttributeValues[variantTypeInfo.variant]?.value === value.value;
            
                          return (
                            <button
                              key={value.id}
                              onClick={() =>
                                !isOut &&
                                handleSelectValue(variantTypeInfo.variant, {
                                  id: value.id,
                                  value: value.value,
                                })
                              }
                              disabled={isOut}
                              className={`px-4 py-2 rounded-md border text-sm font-medium transition-all
                                ${
                                  isOut ? "bg-gray-100 text-gray-400 cursor-not-allowed" : isSelected ? "bg-green-100 text-green-700 border-green-500" : "bg-white hover:bg-gray-100 border-gray-300 text-gray-800"
                                }`}
                            >
                              {value.value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!isProductOutOfStock && (
              <div className="flex items-center gap-4 mb-6 mt-4">
                <span className="text-lg font-semibold text-gray-800">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={handleDecrement}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xl font-bold transition-colors"
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <input
                    type="text"
                    value={quantity}
                    readOnly
                    className="w-16 text-center border-x border-gray-300 text-lg font-semibold text-gray-800 focus:outline-none bg-white"
                  />
                  <button
                    onClick={handleIncrement}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xl font-bold transition-colors"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col space-y-3 mt-auto">
              {isProductOutOfStock ? (
                <button
                  onClick={handleNotifyMe}
                  className="w-full font-bold py-3 rounded-lg bg-red-600 text-white flex items-center justify-center text-lg hover:bg-red-700 transition shadow-md mt-[10px]"
                >
                  Notify Me When Available
                </button>
              ) : isInCart ? (
                <button
                  onClick={() => {
                    onClose();
                    navigate("/cart");
                  }}
                  className="flex items-center text-[#00A762] border justify-center w-full rounded-lg shadow-md font-dm px-3 py-3 capitalize cursor-pointer border-[#00A762] hover:bg-[#E6F4EB]"
                >
                  Go to Cart
                </button>
              ) : (
                <button
                  onClick={handleAddToCart}
                  disabled={ isProductOutOfStock}
                  className={`w-full font-bold py-3 rounded-lg flex items-center justify-center text-lg transition shadow-md
                    ${
                      isProductOutOfStock
                        ? "bg-gray-400 cursor-not-allowed opacity-70"
                        : "bg-[#00A762] hover:bg-[#28A745] text-white"
                    }`}
                >
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    className="text-[22px] mr-2"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    ></path>
                  </svg>{" "}
                  Add to Cart
                </button>
              )}

              <div className="relative">
                <button
                  onClick={() => {
                    const accessToken = localStorage.getItem("accessToken");
                    if (!accessToken) {
                      navigate("/login");
                      toast.error("You need to log in to manage your wishlist");
                      return;
                    }
                    setShowWishlistModal(true);
                  }}
                  className="w-full font-bold py-3 rounded-lg border border-gray-400 text-gray-800 flex items-center justify-center text-lg hover:bg-gray-100 transition shadow-md"
                >
                  {wishlistStatus ? (
                    <span className="text-red-500 text-[22px] mr-2">♥</span>
                  ) : (
                    <span className="text-gray-500 text-[22px] mr-2">♡</span>
                  )}
                  Add to Wishlist
                </button>
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {showWishlistModal && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-4 right-20 z-50 bg-white border border-gray-200 shadow-lg rounded-xl p-4 w-72 max-h-64 overflow-y-auto custom-scrollbar"
            >
              <button
                onClick={() => setShowWishlistModal(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl"
                aria-label="Close wishlist modal"
              >
                &times;
              </button>
              <h3 className="font-semibold text-lg text-gray-800 mb-3">
                Save to Wishlist
              </h3>

              <div className="space-y-2 pb-2">
                {Object.keys(userWishlists).length > 0 ? (
                  Object.keys(userWishlists).map((listName) => (
                    <button
                      key={listName}
                      onClick={() => handleAddToList(listName, productId)}
                      className="w-full text-left bg-gray-100 p-2 rounded-md hover:bg-gray-200 transition text-sm flex items-center gap-2"
                    >
                      <span className="text-green-600">➕</span> {listName}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No lists found.</p>
                )}
              </div>

              <div className="border-t my-3" />

              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="New list name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                />
                <button
                  onClick={() => handleCreateNewList(productId)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded-md transition"
                >
                  ➕ Create & Add
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default QuickViewModal;